import head from 'lodash/head';
import last from 'lodash/last';

import {TIMEZONES} from 'common/constants';
import {
  ColorChoice,
  DateKeyedFunnelSegments,
  FunnelBookmark,
  FunnelSegment,
  FunnelStep,
} from 'common/report/funnels/models/types';
import {OTHER_PROP, OVERALL_PROP, SEGMENT_SEPARATOR} from 'common/report/funnels/models/constants';
import {bookmarkHasTimeComparison} from 'common/report/funnels/util/bar-chart';
import {formatDateString, getSegmentDisplayName} from 'common/report/funnels/util';
import {CalendarDateRange} from 'common/widgets/date-range-picker/models/types';
import {isCohortSegment, Segment} from 'common/query-builder/query-block/segment-menu/types';
import {CUSTOM_ALERT_FUNNELS_DEFAULT_KEYS} from 'common/widgets/alerts/custom-alert/constants';
import {
  assertIsStandardTimeUnit,
  formatDate,
  getCalendarDate,
  parseDate,
  transformDateByRelativeDate,
} from 'common/util/date';
import {BarStyle, SelectedSegmentStep} from 'common/report/funnels/models/bar-chart/types';
import {getFunnelStep, getMedian} from 'common/report/funnels/models/bar-chart/utils';
import {FunnelQueryResponse} from 'common/report/funnels/models/api-types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';

import {FunnelsQuery} from './funnels-query';

/**
 * helper function to update TopPathRenameMap
 * eventually we should move FunnelsQuery into common and avoid duplicated functions
 */
function _addToTopPathsRenameMap(renameMap: {[key: string]: string}, segment: FunnelSegment, labels: string[]) {
  if (last(segment.props).prop !== `undefined` && ![OTHER_PROP, OVERALL_PROP].includes(head(segment.props).prop)) {
    renameMap[segment.prop] = segment.props
      .slice(0, -1)
      .map((item) => item.propName)
      .concat(
        last(segment.props)
          .prop.split(`,`)
          .map((stepIdx) => labels[stepIdx] || stepIdx)
          .join(` ➞ `),
      )
      .join(SEGMENT_SEPARATOR);
  }
}

/**
 * Computes mapping from segment names in the data to user friendly names.
 * Currently, it returns the mapping for converting any order segments
 * from step indices to step names.
 */
export function getTopPathsRenameMap(
  funnelsQuery: FunnelsQuery,
  funnelSegments: DateKeyedFunnelSegments[],
): {[key: string]: string} {
  const renameMap: {[key: string]: string} = {};
  if (funnelsQuery.isAnyOrderFunnel() && funnelsQuery.isSegmentedByTopPaths()) {
    const labels = funnelsQuery.getStepLabels(true);
    for (const currentDate of funnelSegments || []) {
      for (const segment of currentDate[`segments`]) {
        _addToTopPathsRenameMap(renameMap, segment, labels);
      }
    }
  }
  return renameMap;
}

/** Apply top-paths rename map to all segments */
export function getTopPathRenamedSegments(
  funnelsQuery: FunnelsQuery,
  segmentData: DateKeyedFunnelSegments[],
): FunnelSegment[] {
  const segmentRenameMap = getTopPathsRenameMap(funnelsQuery, segmentData);
  const segments = segmentData?.[0]?.segments;
  return Object.values(segments).map((item) => {
    return item.prop in segmentRenameMap ? {...item, propName: segmentRenameMap[item.prop]} : item;
  });
}

/** helper function to get conversion count on last step */
function _getNumericFunnelConversion(steps) {
  const lastStep = steps[steps.length - 1];
  return lastStep.count;
}

/**
 * Get all data segments, transformed by the top-paths rename and ordered by conversions
 */
export function getRenamedOrderedSegmentSteps(
  funnelsQuery: FunnelsQuery,
  segmentData: DateKeyedFunnelSegments[],
  comparatorOverride?: (a: FunnelSegment, b: FunnelSegment) => number,
) {
  return getTopPathRenamedSegments(funnelsQuery, segmentData).sort(
    comparatorOverride ||
      ((a, b) => {
        if (a.prop === OVERALL_PROP) {
          return -1;
        } else if (b.prop === OVERALL_PROP) {
          return 1;
        } else {
          return _getNumericFunnelConversion(b.steps) - _getNumericFunnelConversion(a.steps);
        }
      }),
  );
}

export function getMedianComparator(
  stepIndex: number,
  medianAccessor: keyof FunnelStep,
): (a: FunnelSegment, b: FunnelSegment) => number {
  return (a: FunnelSegment, b: FunnelSegment): number => {
    if (a.prop === OVERALL_PROP) {
      return -1;
    } else if (b.prop === OVERALL_PROP) {
      return 1;
    } else {
      return (
        getMedian(getFunnelStep(b.steps, stepIndex), medianAccessor) -
        getMedian(getFunnelStep(a.steps, stepIndex), medianAccessor)
      );
    }
  };
}

function _formatTime(from: string | Date, to: string | Date): string {
  const customFormatting = {day: `MMM D`};
  if (!(from instanceof Date)) {
    from = parseDate(from);
  }

  if (!(to instanceof Date)) {
    to = parseDate(to);
  }
  return `${formatDate(from, {unit: `day`, customFormatting})} - ${formatDate(to)}`;
}

function getCalendarDateLabelsFromDates(
  dateRange: DateRange | Partial<FullLengthDateRange>,
  comparisonFrom: Date,
  projectAsOfDate?: string,
): string[] {
  const {unit, value} = dateRange.getRelativeDate();
  assertIsStandardTimeUnit(unit);
  const comparisonTo = transformDateByRelativeDate(comparisonFrom, {unit, value});
  const {from, to} = dateRange.getCalendarDatesAsOf(projectAsOfDate);
  return [_formatTime(from, to), _formatTime(comparisonFrom, comparisonTo)];
}

export function getCalendarDateRanges(
  bookmark: FunnelBookmark,
  funnelsResponse: FunnelQueryResponse,
  projectAsOfDate: string,
  projectTimezone: (typeof TIMEZONES)[number],
): CalendarDateRange[] {
  const comparisonFrom =
    // GOOT fix for bookmark maybe out of sync with response
    // root cause is in react based standard-mode, setState is called before a http request is finished which will update all bookmark updates on dash-cards
    // the response is up to date because it is called upon use action (e.g. deleting time comparison)
    // so sometimes after we save the bookmark is still using the bookmark returned from last API call, but the response is the latest one
    bookmarkHasTimeComparison(bookmark) && funnelsResponse.compare_data
      ? new Date(formatDateString(Object.keys(funnelsResponse.compare_data)[0]))
      : null;
  const dateRange = DateRange.fromBookmarkFormat(bookmark.date_range);
  const {unit, value} = dateRange.getRelativeDate();
  assertIsStandardTimeUnit(unit);
  const comparisonTo = transformDateByRelativeDate(comparisonFrom, {unit, value});
  const {from, to} = dateRange.getCalendarDatesAsOf(projectAsOfDate, null, projectTimezone);
  return [
    {
      from,
      to,
    },
    {
      from: getCalendarDate(comparisonFrom),
      to: getCalendarDate(comparisonTo),
    },
  ];
}

export function getCalendarDateRangeLabels(
  bookmark: FunnelBookmark,
  funnelsResponse: FunnelQueryResponse,
  projectAsOfDate: string,
  projectTimezone: (typeof TIMEZONES)[number],
) {
  return getCalendarDateRanges(bookmark, funnelsResponse, projectAsOfDate, projectTimezone).map(({from, to}) =>
    _formatTime(from, to),
  );
}

// TODO(sam): change FunnelsQuery to explicitly create a `DateRange` so we don't pass in a Partial<FullLengthDateRange> to the `fromDates` function
export function getCalendarDateLabels(
  funnelsQuery: FunnelsQuery,
  comparisonSegmentData: DateKeyedFunnelSegments[],
  projectAsOfDate?: string,
): string[] {
  const {dateRange} = funnelsQuery;
  const comparisonFrom = comparisonSegmentData?.[0].date;
  return getCalendarDateLabelsFromDates(dateRange, comparisonFrom, projectAsOfDate);
}

export function createBucketLineChartFunnelData({
  segmentData,
  selected,
  stepIndex,
  hasComparison = false,
  isComparisonData = false,
}: {
  segmentData: FunnelSegment[];
  selected: ColorChoice[];
  stepIndex: number | null;
  hasComparison: boolean;
  isComparisonData: boolean;
}): Map<string, SelectedSegmentStep> {
  let style: BarStyle = null;
  const selectedSegments = new Map<string, string>(selected.map((s) => [s.key, s.color]));
  const data = new Map<string, SelectedSegmentStep>();

  // if there is comparison data, we need to set style
  if (hasComparison) {
    style = isComparisonData ? BarStyle.Stripe : BarStyle.Solid;
  }

  segmentData.forEach((segment) => {
    data.set(segment.prop, {
      segmentKey: segment.prop,
      segmentName: segment.propName,
      step: getFunnelStep(segment.steps, stepIndex),
      color: selectedSegments.get(segment.prop),
      ...(style ? {style} : null),
    });
  });

  return data;
}

export function canUseSessions() {
  return true;
}

export function getAlertKeyValues(funnelQuery, results) {
  if (!funnelQuery?.groupBy?.length) {
    return CUSTOM_ALERT_FUNNELS_DEFAULT_KEYS;
  }

  const funnelSegmentsData = Object.values(results.data)[0];

  const excludedSegmentKeys = [`$overall`, `$other`, ``];

  let segmentDepth = 1;
  let allSegments = new Set();

  const alertKeyValues = [CUSTOM_ALERT_FUNNELS_DEFAULT_KEYS[0]];

  funnelQuery.groupBy.forEach((group, idx) => {
    const groupContent = Object.values(group)[0];

    const isGroupCohortSegment = isCohortSegment(group);

    segmentDepth = idx + 1;
    allSegments = new Set();
    getSegmentKeys(funnelSegmentsData, 1);

    alertKeyValues.push({
      name: isGroupCohortSegment ? `$cohort` : groupContent[`propertyName`],
      displayName: isGroupCohortSegment ? `Cohort` : getSegmentDisplayName(group as Segment),
      values: Array.from(allSegments),
      displayValues: Array.from(allSegments),
      isPercentage: true,
    } as any);
  });

  return alertKeyValues;

  function getSegmentKeys(currSegments, depth) {
    if (depth === segmentDepth) {
      const segmentKeys = Object.keys(currSegments).filter((seg) => !excludedSegmentKeys.includes(seg));
      segmentKeys.forEach((seg) => allSegments.add(seg));
    } else {
      for (const key in currSegments) {
        if (!excludedSegmentKeys.includes(key)) {
          getSegmentKeys(currSegments[key], depth + 1);
        }
      }
    }
  }
}
